// Date ↔ pixel math for the Gantt timeline. The timeline always starts on a Monday
// and spans whole weeks, so weekly gridlines and weekend shading line up with day 0.
import { addDays, addMonths, differenceInCalendarDays, endOfMonth, format, isSameMonth, startOfMonth, startOfWeek } from 'date-fns';
import type { Task } from '@shared/types';

export type Zoom = 'day' | 'week' | 'month';
export type DragMode = 'move' | 'start' | 'end';

export const PX_PER_DAY: Record<Zoom, number> = { day: 40, week: 16, month: 5 };
const MIN_DAYS: Record<Zoom, number> = { day: 126, week: 280, month: 840 };

export const ROW_H = 34;
export const BAR_H = 22;
export const HEAD_H = 56;

export interface Range {
  /** local midnight of a Monday */
  start: number;
  /** whole weeks */
  days: number;
}

export function desiredRange(minTs: number, maxTs: number, zoom: Zoom): Range {
  const start = startOfWeek(addMonths(startOfMonth(minTs), -1), { weekStartsOn: 1 }).getTime();
  const end = endOfMonth(addMonths(maxTs, 2)).getTime();
  let days = differenceInCalendarDays(end, start) + 1;
  days = Math.max(days, MIN_DAYS[zoom]);
  return { start, days: Math.ceil(days / 7) * 7 };
}

/** Grow-only merge so the origin never jumps under the user's cursor. */
export function mergeRange(a: Range, b: Range): Range {
  const start = Math.min(a.start, b.start);
  const end = Math.max(addDays(a.start, a.days).getTime(), addDays(b.start, b.days).getTime());
  return { start, days: differenceInCalendarDays(end, start) };
}

export const dayIndex = (rangeStart: number, ts: number) => differenceInCalendarDays(ts, rangeStart);
export const dayTs = (rangeStart: number, idx: number) => addDays(rangeStart, idx).getTime();

/** Inclusive day span a task occupies, or null when it has no dates. */
export function spanOf(t: Pick<Task, 'startDate' | 'dueDate'>, rangeStart: number): { a: number; b: number } | null {
  const s = t.startDate != null ? dayIndex(rangeStart, t.startDate) : null;
  const d = t.dueDate != null ? dayIndex(rangeStart, t.dueDate) : null;
  if (s === null && d === null) return null;
  if (s !== null && d !== null) return { a: Math.min(s, d), b: Math.max(s, d) };
  const x = (d ?? s)!;
  return { a: x, b: x };
}

/** How far (in days) a drag may go for a given handle. */
export function dragLimits(t: Pick<Task, 'startDate' | 'dueDate'>, mode: DragMode, rangeStart: number) {
  const INF = 1e6;
  if (mode === 'move') return { min: -INF, max: INF };
  const s = t.startDate != null ? dayIndex(rangeStart, t.startDate) : null;
  const d = t.dueDate != null ? dayIndex(rangeStart, t.dueDate) : null;
  if (mode === 'start') {
    const base = (s ?? d)!;
    return { min: -INF, max: d !== null ? Math.max(0, d - base) : INF };
  }
  const base = (d ?? s)!;
  return { min: s !== null ? Math.min(0, s - base) : -INF, max: INF };
}

/** New dates after dragging by `delta` days (keeps each date's time of day). */
export function applyDrag(t: Pick<Task, 'startDate' | 'dueDate'>, mode: DragMode, delta: number): { startDate: number | null; dueDate: number | null } {
  const sh = (ts: number) => addDays(ts, delta).getTime();
  const s = t.startDate;
  const d = t.dueDate;
  if (mode === 'move') return { startDate: s != null ? sh(s) : null, dueDate: d != null ? sh(d) : null };
  if (mode === 'start') return { startDate: sh((s ?? d)!), dueDate: d };
  return { startDate: s, dueDate: sh((d ?? s)!) };
}

export interface Segment {
  key: string;
  x: number;
  w: number;
  label: string;
  sub?: string;
  today?: boolean;
  weekend?: boolean;
}

/** Month bands for the upper header tier. */
export function monthSegments(range: Range, px: number): Segment[] {
  const out: Segment[] = [];
  const end = addDays(range.start, range.days).getTime();
  for (let m = startOfMonth(range.start); m.getTime() < end; m = addMonths(m, 1)) {
    const a = Math.max(0, dayIndex(range.start, m.getTime()));
    const b = Math.min(range.days, dayIndex(range.start, addMonths(m, 1).getTime()));
    if (b <= a) continue;
    out.push({ key: format(m, 'yyyy-MM'), x: a * px, w: (b - a) * px, label: format(m, px * (b - a) < 120 ? 'MMM yyyy' : 'MMMM yyyy') });
  }
  return out;
}

/** Lower header tier: days (day zoom) or weeks (week / month zoom). */
export function unitSegments(range: Range, zoom: Zoom, px: number, todayIdx: number): Segment[] {
  const out: Segment[] = [];
  if (zoom === 'day') {
    for (let i = 0; i < range.days; i++) {
      const d = addDays(range.start, i);
      const wd = i % 7;
      out.push({ key: String(i), x: i * px, w: px, label: format(d, 'd'), sub: format(d, 'EEEEEE'), today: i === todayIdx, weekend: wd >= 5 });
    }
    return out;
  }
  for (let w = 0; w * 7 < range.days; w++) {
    const ws = addDays(range.start, w * 7);
    const we = addDays(ws, 6);
    const label = zoom === 'week' ? `${format(ws, 'MMM d')} – ${format(we, isSameMonth(ws, we) ? 'd' : 'MMM d')}` : format(ws, 'd');
    out.push({ key: String(w), x: w * 7 * px, w: 7 * px, label, today: todayIdx >= w * 7 && todayIdx < w * 7 + 7 });
  }
  return out;
}

export function fmtDay(ts: number | null) {
  return ts == null ? '—' : format(ts, 'EEE, MMM d');
}
